import { Box, Button, Form, Grid, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import {
  AtsJobPostingSettingsPersonalInfoFragment,
  AtsJobRequirementsOptionsEnum,
  AtsUpdateJobPostingApplicationFormDocument,
} from 'generated/resources/graphql'

import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface UpdateJobPostingApplicationForm {
  cvRequirement: AtsJobRequirementsOptionsEnum
  coverLetterRequirement: AtsJobRequirementsOptionsEnum
  phoneRequirement: AtsJobRequirementsOptionsEnum
  photoRequirement: AtsJobRequirementsOptionsEnum
  genderRequirement: AtsJobRequirementsOptionsEnum | null
  personalUrlRequirement: AtsJobRequirementsOptionsEnum
}

interface Props {
  job: AtsJobPostingSettingsPersonalInfoFragment
}

export const PersonalInfo = ({ job }: Props) => {
  const [updateJobPosting] = useMutation(AtsUpdateJobPostingApplicationFormDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsJobPosting,
  })

  const options = [
    { label: i18n.t('ats.jobs_form.mandatory'), value: 'mandatory' },
    { label: i18n.t('ats.jobs_form.optional'), value: 'optional' },
    { label: i18n.t('ats.jobs_form.do_not_ask'), value: 'do_not_ask' },
  ]

  const form = useForm<UpdateJobPostingApplicationForm>({
    defaultValues: {
      cvRequirement: job.cvRequirement,
      coverLetterRequirement: job.coverLetterRequirement,
      phoneRequirement: job.phoneRequirement,
      photoRequirement: job.photoRequirement,
      genderRequirement: job.genderRequirement ?? 'optional',
      personalUrlRequirement: job.personalUrlRequirement,
    },

    async onSubmit(values, { setErrors }) {
      track(trackingEvents.ats.jobSettingsApplicationFormSubmit)

      const result = await updateJobPosting({
        variables: {
          id: job.id,
          cvRequirement: values.cvRequirement,
          coverLetterRequirement: values.coverLetterRequirement,
          phoneRequirement: values.phoneRequirement,
          photoRequirement: values.photoRequirement,
          genderRequirement: values.genderRequirement ?? 'optional',
          personalUrlRequirement: values.personalUrlRequirement,
        },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    },
  })

  return (
    <Form onSubmit={() => form.handleSubmit()} padding={null}>
      <Box gap="s8">
        <Text size="400" color="grey1000" weight="semibold">
          {i18n.t('ats.job_posting_settings.application_form.title')}
        </Text>
        <Text size="300" color="grey800">
          {i18n.t('ats.job_posting_settings.application_form.description')}
        </Text>
      </Box>

      <Box gap="s16" paddingBottom="s24">
        <Grid variant="fixed">
          <Box gridColSpan={{ xxs: 1, xs: 6 }}>
            <F0Select
              label={i18n.t('ats.job_posting_settings.application_form.resume_label')}
              placeholder={i18n.t('common.select_basic')}
              value={form.values.cvRequirement}
              onChange={(value: string) => form.setFieldValue('cvRequirement', value)}
              options={options}
              size="md"
            />
          </Box>
          <Box gridColSpan={{ xxs: 1, xs: 6 }}>
            <F0Select
              label={i18n.t('ats.job_posting_settings.application_form.phone_label')}
              placeholder={i18n.t('common.select_basic')}
              value={form.values.phoneRequirement}
              onChange={(value: string) => form.setFieldValue('phoneRequirement', value)}
              options={options}
              size="md"
            />
          </Box>
          <Box gridColSpan={{ xxs: 1, xs: 6 }}>
            <F0Select
              label={i18n.t('ats.job_posting_settings.application_form.personal_url_label')}
              placeholder={i18n.t('common.select_basic')}
              value={form.values.personalUrlRequirement}
              onChange={(value: string) => form.setFieldValue('personalUrlRequirement', value)}
              options={options}
              size="md"
            />
          </Box>
          <Box gridColSpan={{ xxs: 1, xs: 6 }}>
            <F0Select
              label={i18n.t('ats.job_posting_settings.application_form.cover_letter_label')}
              placeholder={i18n.t('common.select_basic')}
              value={form.values.coverLetterRequirement}
              onChange={(value: string) => form.setFieldValue('coverLetterRequirement', value)}
              options={options}
              size="md"
            />
          </Box>
          <Box gridColSpan={{ xxs: 1, xs: 6 }}>
            <F0Select
              label={i18n.t('ats.job_posting_settings.application_form.photo_label')}
              placeholder={i18n.t('common.select_basic')}
              value={form.values.photoRequirement}
              onChange={(value: string) => form.setFieldValue('photoRequirement', value)}
              options={options}
              size="md"
            />
          </Box>
          <Box gridColSpan={{ xxs: 1, xs: 6 }}>
            <F0Select
              label={i18n.t('ats.job_posting_settings.application_form.gender_label')}
              placeholder={i18n.t('common.select_basic')}
              value={form.values.genderRequirement ?? 'optional'}
              onChange={(value: string) => form.setFieldValue('genderRequirement', value)}
              options={options}
              hint={i18n.t('ats.job_posting_settings.application_form.gender_tooltip')}
              size="md"
            />
          </Box>
        </Grid>
      </Box>

      <Button disabled={form.isSubmitting || !form.isValid} submit>
        {i18n.t('common.submit')}
      </Button>
    </Form>
  )
}
